/**
 * utm-tracking.js — Campaign attribution capture.
 *
 * Runs on every page. When a visitor lands with utm_* params (or an ad click
 * ID), the touch is saved in localStorage so it survives browsing to other
 * pages before converting:
 *
 *   first — the original campaign touch (kept for TOUCH_TTL_MS)
 *   last  — the most recent campaign touch
 *
 * Conversions read it back through window.AATAttribution:
 *   - Quote requests (quote-modal.js, rfq-page.js) add quoteFields() to the
 *     quote_request payload sent to the quote proxy.
 *   - Retail orders get the same values as hidden cart attributes, which
 *     Shopify copies onto the order (Order → Additional details).
 */
(function () {
  'use strict';

  var STORAGE_KEY   = 'aat_attribution';
  var SYNC_KEY      = 'aat_attribution_cart_sync';
  var TOUCH_TTL_MS  = 90 * 24 * 60 * 60 * 1000;
  var MAX_VALUE_LEN = 191;

  var UTM_PARAMS   = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];
  var CLICK_PARAMS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid'];

  // ── Storage ────────────────────────────────────────────────────────────────

  function read(key) {
    try { return JSON.parse(localStorage.getItem(key) || 'null'); } catch (e) { return null; }
  }

  function write(key, value) {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) {}
  }

  function clean(value) {
    return String(value || '').trim().slice(0, MAX_VALUE_LEN);
  }

  function isExpired(touch) {
    return !touch || !touch.captured_at ||
      Date.now() - new Date(touch.captured_at).getTime() > TOUCH_TTL_MS;
  }

  // ── Capture ────────────────────────────────────────────────────────────────

  function touchFromUrl() {
    var params;
    try { params = new URLSearchParams(window.location.search); } catch (e) { return null; }

    var touch = {};
    var found = false;
    UTM_PARAMS.concat(CLICK_PARAMS).forEach(function (name) {
      var value = clean(params.get(name));
      if (value) { touch[name] = value; found = true; }
    });
    if (!found) return null;

    touch.landing_page = clean(window.location.pathname + window.location.search);
    touch.referrer     = clean(document.referrer);
    touch.captured_at  = new Date().toISOString();
    return touch;
  }

  function capture() {
    var data  = read(STORAGE_KEY) || {};
    var touch = touchFromUrl();

    if (data.first && isExpired(data.first)) data.first = null;
    if (data.last && isExpired(data.last)) data.last = null;

    if (touch) {
      if (!data.first) data.first = touch;
      data.last = touch;
    }

    if (data.first || data.last) write(STORAGE_KEY, data);
    return data;
  }

  var attribution = capture();

  // ── Public API ─────────────────────────────────────────────────────────────

  // Flat fields for the quote_request payload. utm_* are the original (first)
  // touch; last_utm_* are the most recent touch, when there is one.
  function quoteFields() {
    var first  = attribution.first || attribution.last;
    var last   = attribution.last;
    var fields = {};
    if (!first) return fields;

    UTM_PARAMS.concat(CLICK_PARAMS).forEach(function (name) {
      if (first[name]) fields[name] = first[name];
    });
    fields.landing_page    = first.landing_page || '';
    fields.referrer        = first.referrer || '';
    fields.first_touch_at  = first.captured_at || '';

    if (last && last.captured_at !== first.captured_at) {
      UTM_PARAMS.forEach(function (name) {
        if (last[name]) fields['last_' + name] = last[name];
      });
      fields.last_touch_at = last.captured_at || '';
    }
    return fields;
  }

  // Comma-separated utm_campaign values for the CRM's campaign_codes field,
  // which it splits on commas into Campaign records.
  function campaignCodes() {
    var codes = [];
    [attribution.first, attribution.last].forEach(function (touch) {
      var code = touch && touch.utm_campaign && touch.utm_campaign.replace(/,/g, ' ').trim();
      if (code && codes.indexOf(code) === -1) codes.push(code);
    });
    return codes.join(',');
  }

  window.AATAttribution = {
    get: function () { return attribution; },
    quoteFields: quoteFields,
    campaignCodes: campaignCodes
  };

  // ── Cart attributes (retail orders) ────────────────────────────────────────

  // Underscore-prefixed keys stay hidden from the customer at checkout.
  function cartAttributes() {
    var attrs = {};
    var fields = quoteFields();
    Object.keys(fields).forEach(function (key) {
      if (fields[key]) attrs['_' + key] = fields[key];
    });
    return attrs;
  }

  function cartTokenCookie() {
    var match = document.cookie.match(/(?:^|;\s*)cart=([^;]*)/);
    if (!match) return '';
    try { return decodeURIComponent(match[1]).split('?')[0]; } catch (e) { return ''; }
  }

  // Re-sync whenever the attribution changes or Shopify issues a new cart
  // (e.g. after a completed checkout), so each order carries the values.
  function syncCart() {
    var attrs = cartAttributes();
    if (!Object.keys(attrs).length) return;

    var signature = JSON.stringify(attrs);
    var synced    = read(SYNC_KEY) || {};
    var token     = cartTokenCookie();
    if (token && synced.token === token && synced.signature === signature) return;

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (cart) {
        if (cart && cart.token) write(SYNC_KEY, { token: cart.token.split('?')[0], signature: signature });
      })
      .catch(function () {});
  }

  syncCart();
})();
